import { useState } from 'react';
import { beneficiosPlus, beneficiosFamiliar, COVERLY_PHONE } from '@/mocks/coverly';

type UpgradeId = 'beneficiario' | 'cremacion';

type Upgrade = {
  id: UpgradeId;
  icon: string;
  title: string;
  shortTitle: string;
  price: number;
  description: string;
};

const upgrades: Upgrade[] = [
  {
    id: 'beneficiario',
    icon: 'ri-user-add-line',
    title: 'Beneficiario adicional',
    shortTitle: 'Agregar beneficiario',
    price: 80,
    description: 'Suma un espacio extra para una persona de tu familia.',
  },
  {
    id: 'cremacion',
    icon: 'ri-fire-line',
    title: 'Cremacion opcional',
    shortTitle: 'Agregar cremacion',
    price: 110,
    description: 'Solicita este beneficio opcional para integrarlo a tu membresia.',
  },
];

export default function ScreenPlan({
  user,
}: {
  user: { plan: string; planNombre: string; planPrecio: number; folio: string; estado: string };
}) {
  const beneficios = user.plan === 'plus' ? beneficiosPlus : beneficiosFamiliar;
  const [copiedFolio, setCopiedFolio] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState<Upgrade | null>(null);
  const [requestedExtras, setRequestedExtras] = useState<UpgradeId[]>([]);
  const [requestMessage, setRequestMessage] = useState('');

  const extrasTotal = requestedExtras.reduce((total, id) => {
    const upgrade = upgrades.find((item) => item.id === id);
    return total + (upgrade?.price ?? 0);
  }, 0);
  const monthlyTotal = user.planPrecio + extrasTotal;

  const copyFolio = () => {
    navigator.clipboard.writeText(user.folio).catch(() => {});
    setCopiedFolio(true);
    setTimeout(() => setCopiedFolio(false), 2000);
  };

  const submitUpgradeRequest = () => {
    if (!selectedUpgrade) return;

    setRequestedExtras((current) =>
      current.includes(selectedUpgrade.id) ? current : [...current, selectedUpgrade.id]
    );
    setRequestMessage(
      `Solicitud enviada. El beneficio "${selectedUpgrade.title}" quedo registrado para revision. El cargo de +$${selectedUpgrade.price} se vera reflejado en tu siguiente corte mensual cuando quede confirmado.`
    );
    setSelectedUpgrade(null);
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#F7F6FF]">
      <div className="px-5 pt-12 pb-5 flex items-center justify-center" style={{ backgroundColor: '#132D86' }}>
        <h1 className="text-lg font-bold text-white">Tu plan</h1>
      </div>

      <div className="flex-1 px-4 py-5 pb-24 flex flex-col gap-4 overflow-y-auto">
        <div
          className="rounded-3xl p-5 animate-slide-up border shadow-sm"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E3E0FA', animationDelay: '0.05s' }}
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-[11px] text-[#6D67A8] mb-1 uppercase font-bold tracking-wide">Tu plan activo</p>
              <p className="text-xl font-extrabold text-[#211B4F]">Plan {user.planNombre}</p>
              <span
                className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-[11px] font-bold"
                style={{ backgroundColor: '#EAF8F2', color: '#177A5E' }}
              >
                <i className="ri-checkbox-circle-line text-sm" />
                {user.estado}
              </span>
            </div>
            <div className="text-right">
              <p className="text-3xl font-extrabold" style={{ color: '#534AB7' }}>
                ${monthlyTotal}
              </p>
              <p className="text-xs text-[#6D67A8]">/mes estimado</p>
              {extrasTotal > 0 && (
                <p className="text-[11px] text-[#177A5E] font-bold mt-1">Incluye +${extrasTotal} en mejoras</p>
              )}
            </div>
          </div>

          <div className="h-px bg-[#ECE9FF] mb-4" />

          <ul className="flex flex-col gap-3">
            {beneficios.map((beneficio, index) => {
              const isHighlighted = user.plan === 'plus' && index >= beneficios.length - 2;

              return (
                <li key={beneficio} className="flex items-start gap-3">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: isHighlighted ? 'rgba(245,158,11,0.14)' : 'rgba(83,74,183,0.12)' }}
                  >
                    <i className="ri-check-line text-xs" style={{ color: isHighlighted ? '#D97706' : '#534AB7' }} />
                  </div>
                  <span className="text-sm leading-relaxed" style={{ color: isHighlighted ? '#B45309' : '#45405F' }}>
                    {beneficio}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div
          className="rounded-2xl p-4 border-l-4 animate-slide-up"
          style={{ backgroundColor: 'rgba(83,74,183,0.06)', borderLeftColor: '#534AB7', animationDelay: '0.1s' }}
        >
          <div className="flex items-center gap-2">
            <i className="ri-lock-line text-base" style={{ color: '#534AB7' }} />
            <p className="text-xs text-[#534AB7] font-semibold">Tu precio esta congelado desde tu contratacion.</p>
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-5">
            Nunca pagaras mas por el mismo plan, sin importar la inflacion.
          </p>
        </div>

        <div className="animate-slide-up" style={{ animationDelay: '0.15s' }}>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 px-1">Mejoras disponibles</p>

          {requestMessage && (
            <div className="mb-3 rounded-2xl p-3 border bg-white flex gap-2" style={{ borderColor: '#C9F0E2' }}>
              <i className="ri-checkbox-circle-line text-lg text-[#1D9E75]" />
              <p className="text-xs leading-relaxed text-[#45405F]">{requestMessage}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {upgrades.map((upgrade) => {
              const requested = requestedExtras.includes(upgrade.id);

              return (
                <div
                  key={upgrade.id}
                  className="bg-white rounded-2xl p-4 border-2 border-dashed"
                  style={{ borderColor: '#6B5EE8' }}
                >
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: 'rgba(83,74,183,0.1)' }}
                  >
                    <i className={`${upgrade.icon} text-base`} style={{ color: '#534AB7' }} />
                  </div>
                  <p className="text-sm font-bold text-[#211B4F] mb-0.5">+${upgrade.price}</p>
                  <p className="text-xs text-gray-500 mb-3 leading-snug">{upgrade.shortTitle}</p>
                  <button
                    type="button"
                    disabled={requested}
                    onClick={() => setSelectedUpgrade(upgrade)}
                    className="flex items-center justify-center gap-1 w-full py-2.5 rounded-xl text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:cursor-default"
                    style={{ backgroundColor: requested ? '#AFA9EC' : '#534AB7' }}
                  >
                    <i className={requested ? 'ri-check-line text-sm' : 'ri-add-line text-sm'} />
                    {requested ? 'Solicitado' : 'Agregar'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 animate-slide-up flex flex-col items-center" style={{ animationDelay: '0.2s' }}>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-5">Contacto de emergencia</p>

          <a
            href={`tel:${COVERLY_PHONE}`}
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-white text-sm font-bold cursor-pointer whitespace-nowrap mb-5"
            style={{ backgroundColor: '#132D86' }}
          >
            <i className="ri-phone-line text-base" />
            Llamar ahora · 24/7
          </a>

          <div className="flex items-center gap-2">
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-0.5">Tu folio</p>
              <p className="font-mono text-sm font-bold text-[#211B4F]">{user.folio}</p>
            </div>
            <button
              type="button"
              onClick={copyFolio}
              className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors"
              style={{ backgroundColor: copiedFolio ? 'rgba(29,158,117,0.1)' : '#F3F4F6' }}
            >
              <i
                className={`${copiedFolio ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`}
                style={{ color: copiedFolio ? '#1D9E75' : '#9CA3AF' }}
              />
            </button>
          </div>
        </div>
      </div>

      {selectedUpgrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5 bg-black/45">
          <div className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl animate-slide-up">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-[#6D67A8]">Solicitud de beneficio</p>
                <h2 className="text-xl font-extrabold text-[#211B4F] mt-1">{selectedUpgrade.title}</h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedUpgrade(null)}
                className="w-9 h-9 rounded-full flex items-center justify-center bg-[#F4F2FF] text-[#534AB7]"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <p className="text-sm leading-relaxed text-[#6B6683] mb-4">{selectedUpgrade.description}</p>

            <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: '#F7F6FF' }}>
              <div className="flex items-center justify-between gap-3 py-2 border-b border-[#E3E0FA]">
                <span className="text-xs text-[#7D7895]">ID del cliente</span>
                <span className="font-mono text-sm font-bold text-[#211B4F]">{user.folio}</span>
              </div>
              <div className="flex items-center justify-between gap-3 py-2 border-b border-[#E3E0FA]">
                <span className="text-xs text-[#7D7895]">Codigo de membresia</span>
                <span className="font-mono text-sm font-bold text-[#211B4F]">CVR-{user.folio.replace('CVR-', '')}</span>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2">
                <span className="text-xs text-[#7D7895]">Nuevo total estimado</span>
                <span className="text-sm font-extrabold text-[#534AB7]">
                  ${requestedExtras.includes(selectedUpgrade.id) ? monthlyTotal : monthlyTotal + selectedUpgrade.price}/mes
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={submitUpgradeRequest}
              className="w-full py-3.5 rounded-2xl text-white text-sm font-bold shadow-lg"
              style={{ backgroundColor: '#534AB7', boxShadow: '0 14px 28px rgba(83,74,183,0.25)' }}
            >
              Enviar solicitud para agregar
            </button>
            <p className="text-[11px] leading-relaxed text-center text-[#7D7895] mt-3">
              Te avisaremos cuando el beneficio quede confirmado en tu membresia.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
